/**
 * What is filed in a project: five kinds, each with its count, on the standard record page.
 *
 * EXTRACTED FROM ProjectDetail.tsx ON 2026-10-27, when a project became the eighteenth species and
 * `/projects/:usid` started being served by ObjectDetail like every other record. The bespoke page
 * went; its contents pane is this, because a folder's CONTENTS are the one thing the standard
 * record page has no notion of.
 *
 * ONE PANE WITH FIVE INNER TABS, not five panes on the record page's own strip. ObjectDetail
 * already offers seven or more; adding five would make twelve and bury the ones every record has.
 * The counts stay on the inner tabs, which is where Ed asked for them -- "Products 0" beside
 * "Quote Requests 6" -- and the strip above stays legible.
 *
 * EVERY TAB CARRIES ITS COUNT, INCLUDING THE ZEROS, because a tab that disappears when empty is a
 * tab nobody learns is there.
 *
 * THE COUNT AND THE ROWS CAN DISAGREE AND THAT IS NOT A BUG. The count is of MEMBERSHIPS, taken
 * from the join table in one query by `List Project Members`. The rows are each member resolved
 * through the record's OWN read, which applies its own visibility -- so a reader who cannot see a
 * contract gets a count of one and a row saying so. The number stays the membership deliberately: a
 * count that shrank per viewer would make two people reading the same folder think one of them was
 * looking at the wrong one.
 *
 * SO MEMBERS ARE RESOLVED THROUGH THE READS THAT ALREADY EXIST, one call per member, rather than
 * asking the container for assembled records. The container service refuses to do that on purpose:
 * each kind has its own document shape, and a container that reassembled five of them would be a
 * sixth answer to what a quote looks like, drifting from all five.
 *
 * AND A ROW LINKS TO /object/<usid>, THE CANONICAL LINK SHAPE. ObjectRoute resolves a logical
 * object to the classification route that serves it, and already copes with a record this persona
 * cannot open.
 */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {Box, Button, Paper, Tooltip, Typography} from "@mui/material";

import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import {KIND_LABEL, KIND_ONE, MEMBER_KINDS, PRODUCT_TYPE_ACRONYM, listOf, when} from "../../util/projectMembers";
import {OBJECT_CLASSIFICATIONS} from "../../util/objectClassifications";
import CreateObject from "./CreateObject";
import ContractSchedulePane from "../Object/ContractSchedulePane";
import ParkourDocuments from "./ParkourDocuments";
import ContractPurchaseOrdersPane from "../Object/ContractPurchaseOrdersPane";
import RecordDrawer from "./RecordDrawer";
import ObjectIdLink, {RecordDrawerContext} from "../Project/ObjectIdLink";
import {useSessionContext} from "../../util/SessionContext";
import QuoteLinesPane from "../Object/QuoteLinesPane";
import QuoteOfferPane from "../Object/QuoteOfferPane";
import DiscussionPane from "../Object/DiscussionPane";
import BuildPlanPane from "../Object/BuildPlanPane";
import ProductForm from "../Project/ProductForm";
import QuoteRequestPane from "../Object/QuoteRequestPane";
import CreateQuoteRequest from "../Project/CreateQuoteRequest";
import ProductPipeline from "../Project/ProductPipeline";
import ProjectTimeline from "../Project/ProjectTimeline";

/** A folder that still takes records. A deprecated or cancelled one takes none. */
//: A PROJECT IS OPEN OR CLOSED (Ed, 2026-10-27, evening). Open takes records; closed takes none.
const FILEABLE_STATUSES = ["OPEN"];

// Object ID, Type, Party, Description, Contract Date, Expiry, Items, POs, Status, Remove.
const CONTRACT_COLUMNS = "1fr 1.1fr 1.3fr 2fr 0.9fr 0.9fr 0.5fr 0.5fr 0.8fr 0.7fr";

/** What the server says about a contract filed here, from its current revision -- the Contracts
 *  tab's columns (Ed, 2026-10-28). Absent on a contract the reader may not see. */
type ContractSummary = {
    RevisionUSID?: string | null; ObjectID?: string | null; Name?: string | null;
    Type?: string | null; Party?: string | null; Description?: string | null;
    ContractDate?: string | null; ExpiryDate?: string | null;
    Items?: number | null; POs?: number | null; Status?: string | null;
};

type Member = {MemberType: string; MemberUSID: string; AddedDatetime?: string | null;
               Summary?: ContractSummary | null};
type Resolved = Member & {
    visible: boolean; ObjectID?: string | null; Name?: string | null; Status?: string | null;
    //: THE REVISION, which is what every specialist pane keys on. The membership holds the LOGICAL
    //: object; the resolve below already reads the current revision to get the name and status, so
    //: this costs nothing and saves the inline pane a second lookup.
    RevisionUSID?: string | null;
};

type Props = {
    /** The project's LOGICAL object usid. Memberships key on it, never on a revision. */
    projectObjectUSID: string;
    /** The current revision's status, so a shut folder stops offering a create it would refuse. */
    status?: string | null;
    /** Seeds the quote-request dialog when the folder names a customer. */
    customerObjectUSID?: string | null;
    api: any;
    /**
     * WHICH KIND IS OPEN, DECIDED BY THE PAGE. Until 2026-10-27 this component drew its own row
     * of kind tabs under the record's strip. Ed moved them into the strip itself, after Detail,
     * so the page owns the selection and this draws the body for the kind it is handed -- or
     * nothing, on any other pane. A kind code, "Timeline", or null.
     */
    tab: string | null;
    /** The counts per kind, for the page to put in its tab labels. One fetch, here. */
    onCounts?: (counts: Record<string, number>) => void;
};

const classificationFor = (code: string) =>
    OBJECT_CLASSIFICATIONS.find(one => one.code === code);

/** What the create dialog makes for a kind. A product is an Item held to the PRT- type
 *  (2026-10-29); every other kind is its own classification. */
const creationFor = (kind: string) => {
    if (kind === "Product") {
        return {classification: "Item", singular: "Product", route: "/items", objectTypeAcronym: PRODUCT_TYPE_ACRONYM};
    }
    const found = classificationFor(kind);
    return found ? {classification: found.code, singular: found.singular, route: found.route, objectTypeAcronym: undefined} : null;
};


export default function ProjectMembers({projectObjectUSID, status, customerObjectUSID, api, tab,
                                        onCounts}: Props) {
    const navigate = useNavigate();
    const sessionContext = useSessionContext();
    // THIS ROW'S SELECTION IS ITS OWN, and that is Ed's rule of 2026-10-27: "When clicking on the
    // lower tabs, do not do anything with the selected above tab. Let it keep showing as it was
    // prior to the tab being clicked."
    //
    // IT WAS BRIEFLY THE PAGE'S. Making a kind a PANE meant choosing one deselected the top row and
    // took the record's own fields off the screen -- so looking at what was filed in a project cost
    // you the project. Two independent selections is what the screen actually describes: the top
    // row says what you are reading about the folder, this row says what you are reading inside it.
    //
    // NULL UNTIL ASKED. The row and its counts are the answer to "what is in here"; a list opens
    // only when somebody picks a kind.
    const [members, setMembers] = useState<Member[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [resolved, setResolved] = useState<Record<string, Resolved>>({});
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);
    // WHICH ROW IS OPEN BELOW THE GRID. Keyed on the member's own identifier, and cleared when the
    // kind changes -- a contract left open under the Products grid would be a record from another
    // list entirely.
    const [opened, setOpened] = useState<string | null>(null);
    // THE RECORD'S DETAILS AND FORM, IN A DRAWER ON THE RIGHT. Ed, 2026-10-28: "When Open the Record
    // is clicked, rather than move off the record, bring up the contract details and form values
    // in a right side drawer." The revision to show, or null.
    const [drawer, setDrawer] = useState<string | null>(null);
    // THE OFFER'S ANSWERS RE-READ when the grid below them changes a line or the terms.
    const [quoteTick, setQuoteTick] = useState(0);
    const [creating, setCreating] = useState<string | null>(null);
    //: The product lane a quote request dialog was opened from, so the demand is filed under it.
    const [requestLane, setRequestLane] = useState<string | null>(null);
    // A RECORD MADE AND NOT FILED. Reported by name with a way to open it -- see `fileNew`.
    const [orphan, setOrphan] = useState<{kind: string; usid: string; id?: string} | null>(null);

    const load = useCallback(() => {
        if (!projectObjectUSID) return;
        api.get("t1/v1/project_members",
                {params: {USID: projectObjectUSID},
                 headers: {"idoc-response": "v1.ProjectMember-001"}})
            .then((filed: any) => {
                const body = filed?.data?.PlatformMessage;
                setMembers(listOf<Member>(body?.ListOfProjectMember?.ProjectMember));
                // THE COUNTS COME FROM HERE NOW. They used to ride on the project document; a
                // species document has no business carrying them, and this is where they were
                // always computed from.
                setCounts(body?.Counts ?? {});
                onCounts?.(body?.Counts ?? {});
            })
            .catch((problem: any) => setRefusal(
                problem?.response?.data?.Cause ?? "What is filed here could not be read."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, projectObjectUSID]);

    useEffect(() => { load(); }, [load]);
    // A record opened inline belongs to the kind it was opened under.
    useEffect(() => { setOpened(null); }, [tab]);

    /**
     * Resolve each member through its own record's read.
     *
     * ONE CALL PER MEMBER, AND THAT IS THE DESIGN RATHER THAN A SHORTCUT. Each kind's read applies
     * its own visibility, so this is the only way the rows can be honest about what this reader may
     * see. A folder holds a handful of records per kind; if that stops being true the answer is
     * paging the tab, not teaching the container to assemble records.
     *
     * EVERY KIND RESOLVES THE SAME WAY SINCE 2026-10-27. A quote request used to be the exception
     * -- "not a species, so it is resolved through its own list rather than through
     * object_revisions" -- because it was a plain row with no logical object behind it. It is the
     * nineteenth species now, so the branch is gone and the five are read identically.
     */
    useEffect(() => {
        if (!members.length) { setResolved({}); return; }
        let live = true;

        const resolve = (member: Member) => {
            return api.get("t1/v1/object_revisions",
                           {params: {ObjectUSID: member.MemberUSID, IsCurrent: "true"},
                            headers: {"idoc-response": "v1.ObjectRevision-001"}})
                .then((answer: any) => {
                    const body = answer?.data?.PlatformMessage;
                    const record = listOf<any>(body?.ListOfObjectRevision?.ObjectRevision)[0];
                    if (!record) return {...member, visible: false};
                    return {
                        ...member, visible: true,
                        ObjectID: record.ObjectID ?? null,
                        Name: record.Name ?? null,
                        Status: record.Status ?? null,
                        RevisionUSID: record.USID ?? null,
                    } as Resolved;
                })
                // A MEMBER THIS READER CANNOT SEE IS NOT AN ERROR ON THIS PAGE. It is the expected
                // answer when a record is owned by a part of the organisation they have no reach
                // into, and the row says so rather than the tab failing.
                .catch(() => ({...member, visible: false} as Resolved));
        };

        Promise.all(members.map(resolve)).then((all: Resolved[]) => {
            if (!live) return;
            const byUsid: Record<string, Resolved> = {};
            all.forEach(one => { byUsid[one.MemberUSID] = one; });
            setResolved(byUsid);
        });

        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [members, api]);

    /**
     * File a record this pane has just created.
     *
     * TWO CALLS AND NOT ONE, because the services are two: creating a record and filing it are
     * separate actions with separate privileges, and there is no "create into a project" door. So
     * the create can succeed and the filing fail -- a 403 on `Add To Project`, a folder deprecated
     * from another tab -- and the record then exists, outside any folder.
     *
     * AND THAT ORPHAN IS NAMED RATHER THAN SWALLOWED, which is the whole reason this is not a
     * one-liner. The ingredient list pages left the rail when a project became the only way in, so
     * a record that is not in a folder is genuinely hard to find; telling somebody "that did not
     * work" and dropping the identifier would be telling them they have lost it.
     *
     * THE LOGICAL OBJECT, NEVER THE REVISION. A membership survives every Revise.
     */
    const fileNew = useCallback((kind: string,
                                 created: {ObjectUSID?: string; ObjectID?: string}) => {
        const memberUSID = created.ObjectUSID;
        if (!memberUSID) {
            setRefusal(`The ${KIND_ONE[kind] ?? "record"} was created, but came back without an `
                       + `identifier, so it could not be filed here.`);
            return;
        }
        setBusy(true);
        setRefusal(null);
        setOrphan(null);
        api.post("t1/v1/project_members",
                 {USID: projectObjectUSID, MemberType: kind, MemberUSID: memberUSID})
            .then(() => load())
            .catch((problem: any) => {
                setOrphan({kind, usid: memberUSID, id: created.ObjectID});
                setRefusal(problem?.response?.data?.Cause
                           ?? `The ${KIND_ONE[kind] ?? "record"} was created but could not be `
                              + `filed in this project.`);
            })
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, projectObjectUSID, load]);

    /** A quote request is created and filed the same way, through its own door. */
    const createQuoteRequest = useCallback((body: Record<string, any>) => {
        setCreating(null);
        setBusy(true);
        setRefusal(null);
        setOrphan(null);
        api.post("t1/v1/quote_requests", body,
                 {headers: {"idoc-response": "v1.QuoteRequest-001"}})
            .then((answer: any) => {
                const made = answer?.data?.PlatformMessage?.QuoteRequest;
                // THE LOGICAL OBJECT, NOT THE SATELLITE ROW. A quote request became the nineteenth
                // species on 2026-10-27, so PROJECT_MEMBER keys it the way it keys the other four
                // -- on the OBJECT, which survives a revise. It used to key on this table's own
                // USID because there was no object behind it, and that was the one place the
                // membership meant two different things depending on the kind.
                const objectUSID = made?.ObjectUSID;
                if (!objectUSID) throw new Error("no identifier came back");
                return api.post("t1/v1/project_members",
                                {USID: projectObjectUSID, MemberType: "QuoteRequest",
                                 MemberUSID: objectUSID})
                    .then(() => load())
                    // FILED OR NOT, IT EXISTS -- and it has a page of its own now, so the message
                    // can offer to open it like it does for the other four.
                    .catch((problem: any) => {
                        setOrphan({kind: "QuoteRequest", usid: objectUSID, id: made.ObjectID});
                        throw problem;
                    });
            })
            .catch((problem: any) => setRefusal(
                problem?.response?.data?.Cause
                ?? "The quote request could not be logged against this project."))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, projectObjectUSID, load]);

    const unfile = useCallback((member: Member) => {
        setBusy(true);
        setRefusal(null);
        api.delete("t1/v1/project_members",
                   {params: {MemberType: member.MemberType, MemberUSID: member.MemberUSID}})
            .then(() => load())
            .catch((problem: any) => setRefusal(
                problem?.response?.data?.Cause
                ?? "Removing it from the project did not work, and the reason did not come back."))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, load]);

    // WHETHER THIS FOLDER STILL TAKES RECORDS. Named for what it decides rather than for the state
    // it used to test: a cancelled folder takes none either, and so does a deprecated one.
    const takesRecords = FILEABLE_STATUSES.includes(String(status ?? "").toUpperCase());
    const forTab = useMemo(() => members.filter(one => one.MemberType === tab), [members, tab]);
    // THE FOUR KINDS THE PIPELINE BOARD DRAWS, resolved. A member not yet resolved is carried
    // with what the membership knows, so the board can count it while the record is read.
    const ofKind = useCallback((kind: string) => members
        .filter(one => one.MemberType === kind)
        .map(one => resolved[one.MemberUSID] ?? {...one, visible: undefined}), [members, resolved]);

    // THE BOTTOM MARGIN BELONGS TO THE LIST, NOT THE ROW. With no kind open this block is just the
    // tab row, and whatever pane follows supplies its own spacing -- keeping 40px here left a dead
    // band between the row and the Detail card, which read as the page having lost something.
    //
    // A JS COMMENT AND NOT A JSX ONE: `{/* ... */}` between `return (` and the root element is a
    // syntax error, and the dev server reports it 160 lines further down at the closing brace.
    return (
        <RecordDrawerContext.Provider value={setDrawer}>
        <div style={{marginTop: tab ? 24 : 0, marginBottom: tab ? 40 : 0}}>
            {refusal && (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "4px", padding: "10px 14px",
                          marginBottom: "14px", maxWidth: 980}}>
                    <Typography sx={{fontSize: 13, color: Grey17}}>{refusal}</Typography>
                    {orphan && (
                        // THE IDENTIFIER, AND A WAY TO REACH IT. See `fileNew`.
                        <Typography sx={{fontSize: 12.5, color: Grey7, paddingTop: "6px"}}>
                            {`The ${KIND_ONE[orphan.kind] ?? "record"} ${orphan.id ?? orphan.usid} `}
                            exists and is in no project
                            {/* EVERY KIND HAS A PAGE NOW. This used to fork: four kinds got "open
                                it", and a quote request got "try filing it again" because it had no
                                record page to send anybody to. It is the nineteenth species since
                                2026-10-27, so the fork is gone. */}
                            {<> — <Typography component="span"
                                              sx={{fontSize: 12.5, color: Blue3,
                                                   cursor: "pointer"}}
                                              onClick={() => navigate(`/object/${orphan.usid}`)}>
                                      open it
                                  </Typography> and file it from there, or try again.</>}
                        </Typography>
                    )}
                </Box>
            )}

            {/* AND THE BODY ONLY WHEN A KIND IS OPEN. On Detail, Form or History the strip is
                the whole of this component: the counts stay readable and the create door, which
                belongs to one kind, does not appear with no kind chosen. */}
            {/* THE PIPELINE BOARD IS ON PRODUCTS AGAIN, above the products grid. It was the Overview from 2026-10-29
                until Ed removed that tab on 2026-11-04 ("Put the whole calendar pane above the tabs. Remove the
                Overview tab"); it began life as the Products tab, and its lanes are products. */}
            {tab === "Product" && (
                <Paper elevation={1} style={{width: "100%", boxSizing: "border-box",
                                             padding: "16px 20px 20px 20px", marginTop: 16}}>
                    {/* THE PIPELINE BOARD: a lane per product with the chain
                        demand -> offer -> plan, and the chains outside any lane below. It was the
                        Products tab until Ed, 2026-10-29: "change the Detail tab to Overview and it
                        must show this" (the source product's Wizard). Products is a grid now, like
                        the other four kinds. See ProductPipeline. */}
                    <ProductPipeline api={api}
                                     products={ofKind("Product") as any}
                                     requests={ofKind("QuoteRequest") as any}
                                     responses={ofKind("PreparedQuote") as any}
                                     plans={ofKind("BuildPlan") as any}
                                     takesRecords={takesRecords} busy={busy}
                                     onNewProduct={() => { setRefusal(null); setOrphan(null);
                                                           setCreating("Product"); }}
                                     onNewRequest={lane => { setRefusal(null); setOrphan(null);
                                                             setRequestLane(lane);
                                                             setCreating("QuoteRequest"); }}/>
                </Paper>
            )}

            {tab === "Timeline" && (
                <Paper elevation={1} style={{width: "100%", boxSizing: "border-box",
                                             padding: "16px 20px 20px 20px"}}>
                    <ProjectTimeline api={api}
                                     members={MEMBER_KINDS.flatMap(kind => ofKind(kind)) as any}/>
                </Paper>
            )}

            {tab && (MEMBER_KINDS as readonly string[]).includes(tab) && (<>
            <Paper elevation={1} style={{width: "100%", boxSizing: "border-box",
                                         padding: "12px 20px 20px 20px", marginTop: 16}}>
                {/* THE DOOR THE INGREDIENTS NO LONGER HAVE ANYWHERE ELSE. Four of these kinds left
                    the rail when Ed made a project the only way to reach them; the fifth, a quote
                    request, never had a page at all. So the create belongs here, and it files what
                    it makes in the same gesture.

                    A SHUT FOLDER TAKES NOTHING. The service would refuse with CC-PROJ-SHUT, and
                    offering the button anyway invites the refusal. */}
                <Box sx={{display: "flex", alignItems: "center", gap: "10px",
                          paddingBottom: "6px"}}>
                    <Box sx={{flexGrow: 1}}/>
                    <Tooltip title={takesRecords
                        ? "Create one and file it in this project"
                        : `This project is ${String(status ?? "").toLowerCase()}, so nothing more `
                          + "can be filed in it."}>
                        <span>
                            <Button size="small" variant="outlined"
                                    disabled={busy || !takesRecords}
                                    sx={{textTransform: "none"}}
                                    onClick={() => { setRefusal(null); setOrphan(null);
                                                     setCreating(tab); }}>
                                New {KIND_ONE[tab] ?? KIND_LABEL[tab]}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                {forTab.length === 0 && (
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                                     paddingTop: "8px"}}>
                        No {KIND_LABEL[tab].toLowerCase()} are filed in this project.
                    </Typography>
                )}

                {/* THE CONTRACTS TAB HAS ITS OWN COLUMNS. Ed, 2026-10-28: "Object ID, Type,
                    Party, Description, Contract Date, Expiry, Items (which is the number of items
                    associated), POs (which is the number of POs associated), and Status." They
                    come from the membership read's Summary -- one query on the server -- and a
                    contract the reader may not see has none, so its row says so as before. */}
                {forTab.length > 0 && tab === "Contract" && <>
                    <Box sx={{display: "grid", gridTemplateColumns: CONTRACT_COLUMNS,
                              gap: "10px", padding: "8px 10px",
                              borderBottom: `1px solid ${Grey2}`}}>
                        {["Object ID", "Type", "Party", "Description", "Contract Date", "Expiry",
                          "Items", "POs", "Status", ""].map((head, index) => (
                            <Typography key={`${head}${index}`}
                                        sx={{fontSize: 11.5, color: Grey7,
                                             // "POs" is a plural, not an acronym: uppercased it
                                             // reads as a point of sale.
                                             textTransform: head === "POs" ? "none" : "uppercase",
                                             letterSpacing: "0.04em",
                                             textAlign: index === 6 || index === 7 ? "right" : "left"}}>
                                {head === "POs" ? "POs" : head}
                            </Typography>
                        ))}
                    </Box>
                    {forTab.map(member => {
                        const row = resolved[member.MemberUSID];
                        const known = row?.visible;
                        const summary = member.Summary ?? null;
                        const cell = (text: any, align: "left" | "right" = "left") => (
                            <Typography sx={{fontSize: 12.5, color: Grey8, textAlign: align,
                                             overflow: "hidden", textOverflow: "ellipsis",
                                             whiteSpace: "nowrap"}}
                                        title={text == null ? undefined : String(text)}>
                                {text == null || text === "" ? "—" : String(text)}
                            </Typography>
                        );
                        return <Box key={member.MemberUSID}
                                    // THE ROW OPENS THE RECORD BELOW (Ed, 2026-10-27); its identifier
                                    // is the drawer's door and stops the click (Ed, 2026-10-30).
                                    onClick={() => known && setOpened(opened === member.MemberUSID ? null : member.MemberUSID)}
                                    sx={{display: "grid", gridTemplateColumns: CONTRACT_COLUMNS,
                                         gap: "10px", padding: "11px 10px", alignItems: "center",
                                         borderBottom: `1px solid ${Grey2}`}}>
                            {row === undefined
                                ? <Typography sx={{fontSize: 13, color: Grey6}}>…</Typography>
                                : <ObjectIdLink id={summary?.ObjectID ?? row.ObjectID}
                                                revisionUSID={known ? row.RevisionUSID : null} weight={500}/>}
                            {cell(summary?.Type)}
                            {cell(summary?.Party)}
                            {row !== undefined && !known
                                ? <Typography sx={{fontSize: 12.5, color: Grey7}}>
                                      Filed here, and not visible to you
                                  </Typography>
                                : cell(summary?.Description ?? row?.Name)}
                            {cell(when(summary?.ContractDate))}
                            {cell(when(summary?.ExpiryDate))}
                            {cell(summary ? summary.Items ?? 0 : null, "right")}
                            {cell(summary ? summary.POs ?? 0 : null, "right")}
                            {cell(known ? (summary?.Status ?? row?.Status) : null)}
                            <Button size="small" disabled={busy} sx={{textTransform: "none"}}
                                    onClick={() => unfile(member)}>Remove</Button>
                        </Box>;
                    })}
                    <Typography sx={{fontSize: 11.5, color: Grey7, lineHeight: 1.6,
                                     paddingTop: "10px", maxWidth: 820}}>
                        Removing a record takes it out of this project. It does not delete anything —
                        the record keeps its own history and lifecycle.
                    </Typography>
                </>}

                {forTab.length > 0 && tab !== "Contract" && <>
                    <Box sx={{display: "grid",
                              gridTemplateColumns: "1.2fr 2.4fr 1fr 1fr 0.8fr",
                              gap: "12px", padding: "8px 10px",
                              borderBottom: `1px solid ${Grey2}`}}>
                        {["Number", "Name", "Status", "Filed", ""].map((head, index) => (
                            <Typography key={`${head}${index}`}
                                        sx={{fontSize: 11.5, color: Grey7,
                                             textTransform: "uppercase",
                                             letterSpacing: "0.04em"}}>{head}</Typography>
                        ))}
                    </Box>
                    {forTab.map(member => {
                        const row = resolved[member.MemberUSID];
                        const known = row?.visible;
                        return <Box key={member.MemberUSID}
                                    // THE ROW OPENS THE RECORD BELOW (Ed, 2026-10-27); its identifier
                                    // is the drawer's door and stops the click (Ed, 2026-10-30).
                                    onClick={() => known && setOpened(opened === member.MemberUSID ? null : member.MemberUSID)}
                                    sx={{display: "grid",
                                         gridTemplateColumns: "1.2fr 2.4fr 1fr 1fr 0.8fr",
                                         gap: "12px", padding: "11px 10px", alignItems: "center",
                                         borderBottom: `1px solid ${Grey2}`}}>
                            {row === undefined
                                ? <Typography sx={{fontSize: 13, color: Grey6}}>…</Typography>
                                : <ObjectIdLink id={row.ObjectID} revisionUSID={known ? row.RevisionUSID : null}
                                                weight={500}/>}
                            <Box>
                                <Typography sx={{fontSize: 13, color: Grey17}}>
                                    {row === undefined
                                        ? "Reading the record…"
                                        : (known ? (row.Name || "—")
                                                 : "Filed here, and not visible to you")}
                                </Typography>
                                {row !== undefined && !known && (
                                    // THE HONEST ANSWER, AND IT IS NOT AN ERROR. The membership is
                                    // a fact; being allowed to open the record is a different one.
                                    <Typography sx={{fontSize: 11.5, color: Grey7}}>
                                        Sight of a record is decided by that record's own read, not
                                        by the folder it is in.
                                    </Typography>
                                )}
                            </Box>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>
                                {known ? (row.Status || "—") : "—"}
                            </Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>
                                {when(member.AddedDatetime)}
                            </Typography>
                            <Button size="small" disabled={busy} sx={{textTransform: "none"}}
                                    onClick={() => unfile(member)}>Remove</Button>
                        </Box>;
                    })}
                    <Typography sx={{fontSize: 11.5, color: Grey7, lineHeight: 1.6,
                                     paddingTop: "10px", maxWidth: 820}}>
                        Removing a record takes it out of this project. It does not delete anything —
                        the record keeps its own history and lifecycle.
                    </Typography>
                </>}
            </Paper>

            {/* THE RECORD ITSELF, UNDER THE GRID THAT LISTED IT. Ed's rule of 2026-10-27: clicking a
                contract in the list shows it here rather than leaving the project. Each kind gets
                the specialist pane it already owns on its own record page, so this is the same
                component and the same read -- not a second rendering of the same facts that could
                come to disagree with the first.

                KEYED ON THE REVISION, which the row resolve already fetched -- except the quote
                request's pane, which takes the LOGICAL object: its satellite is found through the
                object, the same key the membership holds. Every kind has a pane since 2026-10-27;
                the fallback sentence below is kept for the day a sixth kind arrives without one.

                AND IT CARRIES A WAY OUT. The identifier used to navigate to the record; opening
                inline is what Ed asked for, but losing the only route to the full page with it
                would be a capability removed in passing. */}
            {opened && (() => {
                const row = resolved[opened];
                if (!row?.visible) return null;
                const readOnly = String(row.Status ?? "").toUpperCase() !== "DRAFT";
                const pane = !row.RevisionUSID ? null
                    : tab === "Contract"
                        ? <>
                            <ContractSchedulePane revisionUSID={row.RevisionUSID}
                                                  status={row.Status}/>
                            {/* THE PURCHASE ORDERS PLACED UNDER IT -- the source product's
                                references table for one kind of reference, titled for what it
                                holds, with a chooser over purchase orders and no relationship
                                picker (Ed, 2026-10-28). Keyed on the LOGICAL object (the
                                membership's own key), because edges hang off the object. */}
                            <Box sx={{marginTop: "18px"}}>
                                <ContractPurchaseOrdersPane objectUSID={opened} status={row.Status} onChanged={load}/>
                            </Box>
                            {/* ITS PARKOUR AI DOCS, UNDER THE SCHEDULE. Ed, 2026-10-28: "When the
                                Contract is shown in a project, we need a section that shows its
                                associated Parkour-AI Docs." The same component the record's own
                                Parkour Documents tab mounts, keyed on the same revision -- one
                                read, not a second rendering that could disagree. */}
                            <Box sx={{marginTop: "18px"}}>
                                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8,
                                                 marginBottom: "6px"}}>
                                    Parkour-AI Docs
                                </Typography>
                                <ParkourDocuments objectRevisionUSID={row.RevisionUSID}
                                                  sessionContext={sessionContext}/>
                            </Box>
                          </>
                    : tab === "PreparedQuote"
                        /* THE WHOLE QUOTE RESPONSE SCREEN, inline (Ed, 2026-10-28: "when I click on
                           a Quote Response in the Project screen, I want to see below the list, a
                           screen like what I showed you"): the offer -- what it answers, the
                           linked item, the three questions, sharing, signatures, build plan,
                           customer document -- then the terms and the line grid, then its
                           Parkour-AI Docs. The same panes the record's own page draws. */
                        ? <>
                            <QuoteOfferPane revisionUSID={String(row.RevisionUSID)} objectUSID={opened}
                                            status={row.Status} objectID={row.ObjectID}
                                            refreshToken={quoteTick}/>
                            <QuoteLinesPane revisionUSID={row.RevisionUSID} status={row.Status}
                                            readOnly={readOnly}
                                            onChanged={() => setQuoteTick(tick => tick + 1)}/>
                            <Box sx={{marginTop: "18px"}}>
                                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8,
                                                 marginBottom: "6px"}}>
                                    Parkour-AI Docs
                                </Typography>
                                <ParkourDocuments objectRevisionUSID={row.RevisionUSID}
                                                  sessionContext={sessionContext}/>
                            </Box>
                            {/* DISCUSSION LAST, as the source product has it: comments, then the
                                audit trail folded under them. */}
                            <Box sx={{marginTop: "18px"}}>
                                <DiscussionPane objectUSID={opened}/>
                            </Box>
                          </>
                    : tab === "BuildPlan"
                        ? <BuildPlanPane revisionUSID={row.RevisionUSID} status={row.Status}
                                         readOnly={readOnly}/>
                    : tab === "Product"
                        // AN ITEM OF TYPE PRT-, since 2026-10-29: its fields are its form, read
                        // here the way the record drawer reads one.
                        ? <ProductForm revisionUSID={row.RevisionUSID}/>
                    : tab === "QuoteRequest"
                        ? <QuoteRequestPane objectUSID={opened}/>
                    : null;

                return (
                    <Paper elevation={1} style={{width: "100%", boxSizing: "border-box",
                                                 padding: "12px 20px 20px 20px", marginTop: 16}}>
                        <Box sx={{display: "flex", alignItems: "baseline", gap: "10px",
                                  paddingBottom: "6px"}}>
                            {/* THE IDENTIFIER IS THE DOOR, as on every row here (Ed, 2026-10-30):
                                the number opens the drawer, the icon beside it a new window. The
                                "Open the record" link that used to sit at the right is gone. */}
                            <ObjectIdLink id={row.ObjectID} revisionUSID={row.RevisionUSID} size={14}/>
                            <Typography sx={{fontSize: 13, color: Grey8}}>{row.Name ?? "—"}</Typography>
                            <Box sx={{flex: 1}}/>
                            <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}}
                                        onClick={() => setOpened(null)}>Close</Typography>
                        </Box>
                        {pane ?? (
                            <Typography sx={{fontSize: 13, color: Grey7, paddingTop: "8px"}}>
                                A {KIND_ONE[tab] ?? "record"} has no pane of its own yet. Open the
                                record to see it in full.
                            </Typography>
                        )}
                    </Paper>
                );
            })()}

            <RecordDrawer revisionUSID={drawer} sessionContext={sessionContext}
                          onClose={() => setDrawer(null)}/>

            {/* ONE DIALOG PER KIND AND ONLY THE OPEN ONE MOUNTED. CreateObject loads the object
                types and their forms when it opens, so mounting five would be five fetches for four
                dialogs nobody asked for.

                STAYS HERE ON CREATE. Landing on the new draft is right when there is nothing else
                to say; here the record still has to be filed, and navigating away mid-flight would
                leave the filing to finish against a page nobody is looking at. */}
            {creating && creating !== "QuoteRequest" && (() => {
                const kind = creationFor(creating);
                if (!kind) return null;
                return <CreateObject
                    open
                    stayOnCreate
                    onClose={() => setCreating(null)}
                    classification={kind.classification}
                    singular={kind.singular}
                    route={kind.route}
                    objectTypeAcronym={kind.objectTypeAcronym}
                    onCreated={made => fileNew(creating, made)}
                />;
            })()}
            </>)}

            <CreateQuoteRequest
                open={creating === "QuoteRequest"}
                busy={busy}
                customerObjectUSID={customerObjectUSID ?? undefined}
                products={ofKind("Product").filter((one: any) => one.visible !== false) as any}
                productObjectUSID={requestLane}
                onClose={() => { setCreating(null); setRequestLane(null); }}
                onCreate={body => { setRequestLane(null); createQuoteRequest(body); }}
            />
        </div>
        </RecordDrawerContext.Provider>
    );
}
